// Main-thread half of the off-thread .dna conversion (see dnaToPack.worker.js).
//
// Split from the worker body so the message protocol can be unit-tested against
// a fake worker — importing the worker body from node would drag the wasm in.
//
// There is no main-thread fallback on purpose. A browser without Worker cannot
// run this conversion in any form a user would accept (a multi-second frozen
// tab), so it fails loudly instead of quietly degrading — CLAUDE.md's rule.

import { ORL_WASM_URL } from "./orlRig.js";

/** Vite compiles this specifier into a real worker chunk. */
export function createDnaToPackWorker() {
  if (typeof Worker === "undefined") {
    throw new Error("[orl] .dna conversion needs Web Workers, which this browser does not provide");
  }
  return new Worker(new URL("./dnaToPack.worker.js", import.meta.url), { type: "module" });
}

/**
 * Convert DNA bytes to the packed bundle in a worker.
 *
 * @param {Uint8Array} dnaBytes
 * @param {object}  [opts]
 * @param {(m: string) => void} [opts.onLog]
 * @param {() => Worker} [opts.createWorker]
 * @param {string}  [opts.wasmUrl]
 * @returns {Promise<Uint8Array>}
 */
export function convertDnaInWorker(dnaBytes, { onLog = () => {}, createWorker = createDnaToPackWorker, wasmUrl = ORL_WASM_URL } = {}) {
  if (!(dnaBytes instanceof Uint8Array)) {
    throw new Error("[orl] convertDnaInWorker: expected the DNA as a Uint8Array");
  }
  const worker = createWorker();
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (fn, v) => {
      if (settled) return;
      settled = true;
      try { worker.terminate?.(); } catch { /* already gone */ }
      fn(v);
    };
    worker.onmessage = (e) => {
      const msg = e?.data ?? {};
      if (msg.type === "log") { onLog(msg.message); return; }
      if (msg.type === "done") {
        const pack = new Uint8Array(msg.pack);
        if (!pack.length) { finish(reject, new Error("[orl] the conversion worker returned an empty pack")); return; }
        finish(resolve, pack);
        return;
      }
      if (msg.type === "error") { finish(reject, new Error(msg.message || "[orl] .dna conversion failed")); return; }
      finish(reject, new Error(`[orl] unexpected message from the conversion worker: ${JSON.stringify(msg)}`));
    };
    // A worker that dies (OOM on a big DNA is the realistic one) never posts a
    // result, so without this the drop UI would sit at "converting" forever.
    worker.onerror = (e) => finish(reject, new Error(`[orl] the conversion worker crashed: ${e?.message ?? "unknown error"}`));
    // Copy once, then transfer the copy: the caller's bytes stay valid (a retry
    // after a failed upload must not find a detached buffer) and the 39 MB does
    // not get cloned a second time on the way in.
    const buf = dnaBytes.slice().buffer;
    worker.postMessage({ dna: buf, wasmUrl }, [buf]);
  });
}

export default convertDnaInWorker;
